import React, { useState } from 'react';
import { Avatar } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { FeatureTag } from '../ui/feature-tag';
import { FeatherFile, FeatherInfo, FeatherChevronDown, FeatherChevronUp, FeatherAlertCircle, FeatherPlus, FeatherUser, FeatherX } from '../ui/icons';
import TaskHistoryTimeline from './TaskHistoryTimeline';
import TaskNotifications from '../shared/TaskNotifications';
import TaskDueDate from '../shared/TaskDueDate';
import TaskDetailModal from './TaskDetailModal';
import dailyReviewService from '../../services/dailyReviewService';
import './ResourceReviewCard.css';

const ResourceReviewCard = ({ resourceData, hasWorkPlan, projectId, date, onRefresh }) => {
    const [expandedHistories, setExpandedHistories] = useState(new Set());
    const [loadingTasks, setLoadingTasks] = useState(new Set());
    const [toast, setToast] = useState(null);
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);

    const toggleHistory = (taskId) => {
        setExpandedHistories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(taskId)) {
                newSet.delete(taskId);
            } else {
                newSet.add(taskId);
            }
            return newSet;
        });
    };

    const getStatusVariant = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
            case 'released':
                return 'success';
            case 'in-progress':
                return 'warning';
            default:
                return 'neutral';
        }
    };

    const formatOtherProjects = (otherProjects) => {
        if (!otherProjects || otherProjects.length === 0) {
            return null;
        }
        return otherProjects.map(p => `${p.projectName} (${p.taskCount} ${p.taskCount === 1 ? 'task' : 'tasks'})`).join(', ');
    };

    const otherProjectsText = formatOtherProjects(resourceData.otherProjects);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleAddToWorkPlan = async (taskId, taskTitle) => {
        if (loadingTasks.has(taskId)) return;

        setLoadingTasks(prev => new Set(prev).add(taskId));

        try {
            await dailyReviewService.addTaskToResourceWorkPlan(
                projectId,
                resourceData.userId || resourceData.user?.id,
                taskId,
                date
            );

            // Optimistically update UI - remove task from unpicked list
            if (onRefresh) {
                onRefresh();
            }

            showToast('Task added to work plan successfully', 'success');
        } catch (error) {
            console.error('Error adding task to work plan:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to add task to work plan';
            
            if (error.response?.data?.code === 'NO_WORK_PLAN') {
                showToast('Resource has not started their day', 'error');
            } else {
                showToast(errorMessage, 'error');
            }
        } finally {
            setLoadingTasks(prev => {
                const newSet = new Set(prev);
                newSet.delete(taskId);
                return newSet;
            });
        }
    };

    const handleAssignTask = async (taskId, taskTitle) => {
        if (loadingTasks.has(taskId)) return;

        setLoadingTasks(prev => new Set(prev).add(taskId));

        try {
            await dailyReviewService.assignTaskToResource(
                projectId,
                resourceData.userId || resourceData.user?.id,
                taskId
            );

            // Optimistically update UI - remove task from backlog
            if (onRefresh) {
                onRefresh();
            }

            showToast('Task assigned successfully', 'success');
        } catch (error) {
            console.error('Error assigning task:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to assign task';
            showToast(errorMessage, 'error');
        } finally {
            setLoadingTasks(prev => {
                const newSet = new Set(prev);
                newSet.delete(taskId);
                return newSet;
            });
        }
    };

    const handleUnassignTask = async (taskId, taskTitle) => {
        if (loadingTasks.has(taskId)) return;

        setLoadingTasks(prev => new Set(prev).add(taskId));

        try {
            await dailyReviewService.unassignTaskFromResource(
                projectId,
                resourceData.userId || resourceData.user?.id,
                taskId,
                date
            );

            // Optimistically update UI - refresh data
            if (onRefresh) {
                onRefresh();
            }

            showToast('Task unassigned successfully', 'success');
        } catch (error) {
            console.error('Error unassigning task:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to unassign task';
            
            if (error.response?.data?.code === 'TASK_IN_ACTIVE_WORK_PLAN') {
                showToast('Cannot unassign task. Task is in the resource\'s active work plan for today.', 'error');
            } else {
                showToast(errorMessage, 'error');
            }
        } finally {
            setLoadingTasks(prev => {
                const newSet = new Set(prev);
                newSet.delete(taskId);
                return newSet;
            });
        }
    };

    const showConfirmDialog = (type, taskId, taskTitle) => {
        let title, message, onConfirm;
        
        if (type === 'add-to-workplan') {
            title = 'Add Task to Work Plan';
            message = `Are you sure you want to add "${taskTitle}" to ${resourceData.user?.name || 'this resource'}'s work plan?`;
            onConfirm = () => {
                setConfirmDialog(null);
                handleAddToWorkPlan(taskId, taskTitle);
            };
        } else if (type === 'assign') {
            title = 'Assign Task';
            message = `Are you sure you want to assign "${taskTitle}" to ${resourceData.user?.name || 'this resource'}?`;
            onConfirm = () => {
                setConfirmDialog(null);
                handleAssignTask(taskId, taskTitle);
            };
        } else if (type === 'unassign') {
            title = 'Unassign Task';
            message = `Are you sure you want to unassign "${taskTitle}" from ${resourceData.user?.name || 'this resource'}?`;
            onConfirm = () => {
                setConfirmDialog(null);
                handleUnassignTask(taskId, taskTitle);
            };
        }
        
        setConfirmDialog({ type, title, message, onConfirm, onCancel: () => setConfirmDialog(null) });
    };

    return (
        <div className="resource-review-card">
            <div className="resource-review-card-header">
                <div className="resource-review-card-user-info">
                    <Avatar size="large">
                        {resourceData.user?.name?.charAt(0) || 'U'}
                    </Avatar>
                    <div>
                        <div className="resource-review-card-name">{resourceData.user?.name || 'Unknown'}</div>
                        {resourceData.user?.email && (
                            <div className="resource-review-card-email">{resourceData.user.email}</div>
                        )}
                    </div>
                </div>
                {otherProjectsText && (
                    <div className="resource-review-card-other-projects">
                        <FeatherInfo size={14} />
                        <span>Also working on: {otherProjectsText}</span>
                    </div>
                )}
            </div>

            {hasWorkPlan ? (
                <>
                    {/* Task Stats */}
                    <div className="resource-review-card-stats">
                        <div className="resource-review-stat-card">
                            <div className="resource-review-stat-label">Tasks Picked</div>
                            <div className="resource-review-stat-value">{resourceData.taskStats?.totalTasks || 0}</div>
                        </div>
                        <div className="resource-review-stat-card">
                            <div className="resource-review-stat-label">Total Hours</div>
                            <div className="resource-review-stat-value">{resourceData.taskStats?.totalHours?.toFixed(1) || '0.0'}h</div>
                        </div>
                        <div className="resource-review-stat-card">
                            <div className="resource-review-stat-label">Closed vs Attempted</div>
                            <div className="resource-review-stat-value">
                                {resourceData.closedVsAttempted?.closed || 0} / {resourceData.closedVsAttempted?.attempted || 0}
                            </div>
                            <div className="resource-review-stat-ratio">
                                {resourceData.closedVsAttempted?.ratio || 0}% closed
                            </div>
                        </div>
                        <div className="resource-review-stat-card">
                            <div className="resource-review-stat-label">In Progress</div>
                            <div className="resource-review-stat-value">{resourceData.taskStats?.inProgress || 0}</div>
                        </div>
                        <div className="resource-review-stat-card">
                            <div className="resource-review-stat-label">Open</div>
                            <div className="resource-review-stat-value">{resourceData.taskStats?.open || 0}</div>
                        </div>
                        <div className="resource-review-stat-card">
                            <div className="resource-review-stat-label">Completed</div>
                            <div className="resource-review-stat-value">{resourceData.taskStats?.completed || 0}</div>
                        </div>
                    </div>

                    {/* Tasks Picked Up Today */}
                    <div className="resource-review-card-tasks">
                        <h3 className="resource-review-card-tasks-title">Tasks Picked Up Today</h3>
                        {resourceData.projectTasks && resourceData.projectTasks.length > 0 ? (
                            <div className="resource-review-tasks-list">
                                {resourceData.projectTasks.map((task) => (
                                    <div key={task.id} className="resource-review-task-item picked">
                                        <div className="resource-review-task-header">
                                            <div className="resource-review-task-title-section">
                                                <FeatherFile size={16} className="resource-review-task-icon" />
                                                <div>
                                                    <div className="resource-review-task-title-row">
                                                        <div 
                                                            className="resource-review-task-title"
                                                            onClick={() => setSelectedTask(task)}
                                                        >
                                                            {task.title}
                                                        </div>
                                                        {task.feature_tag?.due_date && (
                                                            <TaskDueDate dueDate={task.feature_tag.due_date} />
                                                        )}
                                                    </div>
                                                    {task.display_id && (
                                                        <div className="resource-review-task-id">{task.display_id}</div>
                                                    )}
                                                    {task.description && (
                                                        <div className="resource-review-task-description">{task.description}</div>
                                                    )}
                                                    {task.feature_tag && (
                                                        <div className="resource-review-task-feature-tag">
                                                            <FeatureTag
                                                                name={task.feature_tag.name}
                                                                color={task.feature_tag.color || '#6366f1'}
                                                                size="small"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="resource-review-task-badges">
                                                <Badge variant={getStatusVariant(task.status)}>
                                                    {task.status}
                                                </Badge>
                                                {task.hours > 0 && (
                                                    <Badge variant="neutral">
                                                        {task.hours}h
                                                    </Badge>
                                                )}
                                                {task.attempts > 0 && (
                                                    <Badge variant="neutral">
                                                        {task.attempts} {task.attempts === 1 ? 'attempt' : 'attempts'}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        {task.notification && (
                                            <TaskNotifications
                                                taskId={task.id}
                                                taskTitle={task.title}
                                                notification={task.notification}
                                                isLoading={false}
                                            />
                                        )}
                                        {task.history && task.history.length > 0 && (
                                            <div className="resource-review-task-history-section">
                                                <button
                                                    className="resource-review-task-history-toggle"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleHistory(task.id);
                                                    }}
                                                >
                                                    <span>Task History ({task.history.length} {task.history.length === 1 ? 'entry' : 'entries'})</span>
                                                    {expandedHistories.has(task.id) ? (
                                                        <FeatherChevronUp size={16} />
                                                    ) : (
                                                        <FeatherChevronDown size={16} />
                                                    )}
                                                </button>
                                                {expandedHistories.has(task.id) && (
                                                    <TaskHistoryTimeline history={task.history} />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="resource-review-card-empty">No tasks picked up for this project</div>
                        )}
                    </div>

                    {/* Assigned but Not Picked Up Tasks - Compact */}
                    {resourceData.unpickedTasks && resourceData.unpickedTasks.length > 0 && (
                        <div className="resource-review-card-unpicked resource-review-card-compact">
                            <h3 className="resource-review-card-tasks-title resource-review-card-tasks-title-compact">
                                Assigned but Not Picked Up ({resourceData.unpickedTasks.length})
                            </h3>
                            <div className="resource-review-tasks-list resource-review-tasks-list-compact">
                                {resourceData.unpickedTasks.map((task) => (
                                    <div 
                                        key={task.id} 
                                        className="resource-review-task-item resource-review-task-item-unpicked resource-review-task-item-compact resource-review-task-item-with-action"
                                    >
                                        <div 
                                            className="resource-review-task-content"
                                            onClick={() => setSelectedTask(task)}
                                        >
                                            <div className="resource-review-task-header resource-review-task-header-compact">
                                                <div className="resource-review-task-title-section">
                                                    <FeatherFile size={14} className="resource-review-task-icon" />
                                                    <div>
                                                        <div className="resource-review-task-title-row">
                                                            <div className="resource-review-task-title resource-review-task-title-compact">
                                                                {task.title}
                                                            </div>
                                                            {task.feature_tag?.due_date && (
                                                                <TaskDueDate dueDate={task.feature_tag.due_date} />
                                                            )}
                                                        </div>
                                                        {task.display_id && (
                                                            <div className="resource-review-task-id resource-review-task-id-compact">{task.display_id}</div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="resource-review-task-badges resource-review-task-badges-compact">
                                                    <Badge variant={getStatusVariant(task.status)}>
                                                        {task.status}
                                                    </Badge>
                                                    <span className="resource-review-task-days resource-review-task-days-compact">
                                                        {task.daysSinceAssignment === 0 
                                                            ? 'Today' 
                                                            : `${task.daysSinceAssignment}d`
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                            {task.notification && (
                                                <TaskNotifications
                                                    taskId={task.id}
                                                    taskTitle={task.title}
                                                    notification={task.notification}
                                                    isLoading={false}
                                                />
                                            )}
                                        </div>
                                        <div className="resource-review-task-actions">
                                            <button
                                                className="resource-review-task-action-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showConfirmDialog('add-to-workplan', task.id, task.title);
                                                }}
                                                disabled={loadingTasks.has(task.id)}
                                                title="Add to Work Plan"
                                            >
                                                {loadingTasks.has(task.id) ? (
                                                    <div className="resource-review-task-action-spinner" />
                                                ) : (
                                                    <FeatherPlus size={14} />
                                                )}
                                            </button>
                                            <button
                                                className="resource-review-task-action-button resource-review-task-action-button-danger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showConfirmDialog('unassign', task.id, task.title);
                                                }}
                                                disabled={loadingTasks.has(task.id)}
                                                title="Unassign Task"
                                            >
                                                {loadingTasks.has(task.id) ? (
                                                    <div className="resource-review-task-action-spinner" />
                                                ) : (
                                                    <FeatherX size={14} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Backlog - Compact */}
                    {resourceData.backlog && resourceData.backlog.length > 0 && (
                        <div className="resource-review-card-backlog resource-review-card-compact">
                            <h3 className="resource-review-card-tasks-title resource-review-card-tasks-title-compact">
                                Backlog - Unassigned Tasks ({resourceData.backlog.length})
                            </h3>
                            <div className="resource-review-tasks-list resource-review-tasks-list-compact">
                                {resourceData.backlog.map((task) => {
                                    const getDaysSinceCreation = () => {
                                        if (!task.created_at) return 0;
                                        const created = new Date(task.created_at);
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        created.setHours(0, 0, 0, 0);
                                        const diffTime = today - created;
                                        return Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                    };
                                    const daysSince = getDaysSinceCreation();
                                    
                                    return (
                                        <div 
                                            key={task.id} 
                                            className="resource-review-task-item resource-review-task-item-backlog resource-review-task-item-compact resource-review-task-item-with-action"
                                        >
                                            <div 
                                                className="resource-review-task-content"
                                                onClick={() => setSelectedTask(task)}
                                            >
                                                <div className="resource-review-task-header resource-review-task-header-compact">
                                                    <div className="resource-review-task-title-section">
                                                        <FeatherFile size={14} className="resource-review-task-icon" />
                                                        <div>
                                                            <div className="resource-review-task-title-row">
                                                                <div className="resource-review-task-title resource-review-task-title-compact">
                                                                    {task.title}
                                                                </div>
                                                                {task.feature_tag?.due_date && (
                                                                    <TaskDueDate dueDate={task.feature_tag.due_date} />
                                                                )}
                                                            </div>
                                                            {task.display_id && (
                                                                <div className="resource-review-task-id resource-review-task-id-compact">{task.display_id}</div>
                                                            )}
                                                            {task.feature_tag && (
                                                                <div className="resource-review-task-feature-tag">
                                                                    <FeatureTag
                                                                        name={task.feature_tag.name}
                                                                        color={task.feature_tag.color || '#6366f1'}
                                                                        size="small"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="resource-review-task-badges resource-review-task-badges-compact">
                                                        <Badge variant={getStatusVariant(task.status)}>
                                                            {task.status}
                                                        </Badge>
                                                        <span className="resource-review-task-days resource-review-task-days-compact">
                                                            {daysSince === 0 ? 'Today' : `${daysSince}d`}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                className="resource-review-task-action-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showConfirmDialog('assign', task.id, task.title);
                                                }}
                                                disabled={loadingTasks.has(task.id)}
                                                title="Assign to Resource"
                                            >
                                                {loadingTasks.has(task.id) ? (
                                                    <div className="resource-review-task-action-spinner" />
                                                ) : (
                                                    <FeatherUser size={14} />
                                                )}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <>
                    {/* Status: No Work Plan Started */}
                    <div className="resource-review-card-no-work-plan-status">
                        <div className="resource-review-card-status-content">
                            <FeatherAlertCircle size={20} />
                            <div>
                                <div className="resource-review-card-status-title">No Work Plan Started for This Project</div>
                                <div className="resource-review-card-status-description">
                                    This resource has not picked up any tasks from this project for today.
                                    {otherProjectsText && (
                                        <span className="resource-review-card-status-other-projects">
                                            {' '}However, they are working on: {otherProjectsText}.
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Unpicked Tasks - Compact */}
                    <div className="resource-review-card-unpicked resource-review-card-compact">
                        <h3 className="resource-review-card-tasks-title resource-review-card-tasks-title-compact">
                            Assigned but Not Picked Up {resourceData.unpickedTasks && resourceData.unpickedTasks.length > 0 && `(${resourceData.unpickedTasks.length})`}
                        </h3>
                        {resourceData.unpickedTasks && resourceData.unpickedTasks.length > 0 ? (
                            <div className="resource-review-tasks-list resource-review-tasks-list-compact">
                                {resourceData.unpickedTasks.map((task) => (
                                    <div 
                                        key={task.id} 
                                        className="resource-review-task-item resource-review-task-item-unpicked resource-review-task-item-compact resource-review-task-item-with-action"
                                    >
                                        <div 
                                            className="resource-review-task-content"
                                            onClick={() => setSelectedTask(task)}
                                        >
                                            <div className="resource-review-task-header resource-review-task-header-compact">
                                                <div className="resource-review-task-title-section">
                                                    <FeatherFile size={14} className="resource-review-task-icon" />
                                                    <div>
                                                        <div className="resource-review-task-title-row">
                                                            <div className="resource-review-task-title resource-review-task-title-compact">
                                                                {task.title}
                                                            </div>
                                                            {task.feature_tag?.due_date && (
                                                                <TaskDueDate dueDate={task.feature_tag.due_date} />
                                                            )}
                                                        </div>
                                                        {task.display_id && (
                                                            <div className="resource-review-task-id resource-review-task-id-compact">{task.display_id}</div>
                                                        )}
                                                        {task.feature_tag && (
                                                            <div className="resource-review-task-feature-tag">
                                                                <FeatureTag
                                                                    name={task.feature_tag.name}
                                                                    color={task.feature_tag.color || '#6366f1'}
                                                                    size="small"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="resource-review-task-badges resource-review-task-badges-compact">
                                                    <Badge variant={getStatusVariant(task.status)}>
                                                        {task.status}
                                                    </Badge>
                                                    <span className="resource-review-task-days resource-review-task-days-compact">
                                                        {task.daysSinceAssignment === 0 
                                                            ? 'Today' 
                                                            : `${task.daysSinceAssignment}d`
                                                        }
                                                    </span>
                                                </div>
                                            </div>
                                            {task.notification && (
                                                <TaskNotifications
                                                    taskId={task.id}
                                                    taskTitle={task.title}
                                                    notification={task.notification}
                                                    isLoading={false}
                                                />
                                            )}
                                        </div>
                                        <div className="resource-review-task-actions">
                                            <button
                                                className="resource-review-task-action-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showConfirmDialog('add-to-workplan', task.id, task.title);
                                                }}
                                                disabled={loadingTasks.has(task.id)}
                                                title="Add to Work Plan"
                                            >
                                                {loadingTasks.has(task.id) ? (
                                                    <div className="resource-review-task-action-spinner" />
                                                ) : (
                                                    <FeatherPlus size={14} />
                                                )}
                                            </button>
                                            <button
                                                className="resource-review-task-action-button resource-review-task-action-button-danger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showConfirmDialog('unassign', task.id, task.title);
                                                }}
                                                disabled={loadingTasks.has(task.id)}
                                                title="Unassign Task"
                                            >
                                                {loadingTasks.has(task.id) ? (
                                                    <div className="resource-review-task-action-spinner" />
                                                ) : (
                                                    <FeatherX size={14} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="resource-review-card-empty">No assigned tasks</div>
                        )}
                    </div>

                    {/* Backlog - Compact */}
                    {resourceData.backlog && resourceData.backlog.length > 0 && (
                        <div className="resource-review-card-backlog resource-review-card-compact">
                            <h3 className="resource-review-card-tasks-title resource-review-card-tasks-title-compact">
                                Backlog - Unassigned Tasks ({resourceData.backlog.length})
                            </h3>
                            <div className="resource-review-tasks-list resource-review-tasks-list-compact">
                                {resourceData.backlog.map((task) => {
                                    const getDaysSinceCreation = () => {
                                        if (!task.created_at) return 0;
                                        const created = new Date(task.created_at);
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        created.setHours(0, 0, 0, 0);
                                        const diffTime = today - created;
                                        return Math.floor(diffTime / (1000 * 60 * 60 * 24));
                                    };
                                    const daysSince = getDaysSinceCreation();
                                    
                                    return (
                                        <div 
                                            key={task.id} 
                                            className="resource-review-task-item resource-review-task-item-backlog resource-review-task-item-compact resource-review-task-item-with-action"
                                        >
                                            <div 
                                                className="resource-review-task-content"
                                                onClick={() => setSelectedTask(task)}
                                            >
                                                <div className="resource-review-task-header resource-review-task-header-compact">
                                                    <div className="resource-review-task-title-section">
                                                        <FeatherFile size={14} className="resource-review-task-icon" />
                                                        <div>
                                                            <div className="resource-review-task-title-row">
                                                                <div className="resource-review-task-title resource-review-task-title-compact">
                                                                    {task.title}
                                                                </div>
                                                                {task.feature_tag?.due_date && (
                                                                    <TaskDueDate dueDate={task.feature_tag.due_date} />
                                                                )}
                                                            </div>
                                                            {task.display_id && (
                                                                <div className="resource-review-task-id resource-review-task-id-compact">{task.display_id}</div>
                                                            )}
                                                            {task.feature_tag && (
                                                                <div className="resource-review-task-feature-tag">
                                                                    <FeatureTag
                                                                        name={task.feature_tag.name}
                                                                        color={task.feature_tag.color || '#6366f1'}
                                                                        size="small"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="resource-review-task-badges resource-review-task-badges-compact">
                                                        <Badge variant={getStatusVariant(task.status)}>
                                                            {task.status}
                                                        </Badge>
                                                        <span className="resource-review-task-days resource-review-task-days-compact">
                                                            {daysSince === 0 ? 'Today' : `${daysSince}d`}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="resource-review-task-actions">
                                                <button
                                                    className="resource-review-task-action-button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        showConfirmDialog('assign', task.id, task.title);
                                                    }}
                                                    disabled={loadingTasks.has(task.id)}
                                                    title="Assign to Resource"
                                                >
                                                    {loadingTasks.has(task.id) ? (
                                                        <div className="resource-review-task-action-spinner" />
                                                    ) : (
                                                        <FeatherUser size={14} />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
            {toast && (
                <div className={`resource-review-toast resource-review-toast-${toast.type}`}>
                    {toast.message}
                </div>
            )}

            {/* Confirmation Dialog */}
            {confirmDialog && (
                <div 
                    className="resource-review-confirm-overlay"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setConfirmDialog(null);
                        }
                    }}
                >
                    <div className="resource-review-confirm-dialog">
                        <h3 className="resource-review-confirm-title">{confirmDialog.title}</h3>
                        <p className="resource-review-confirm-message">{confirmDialog.message}</p>
                        <div className="resource-review-confirm-actions">
                            <Button
                                variant="neutral-tertiary"
                                onClick={confirmDialog.onCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant={confirmDialog.type === 'unassign' ? 'danger' : 'brand-primary'}
                                onClick={confirmDialog.onConfirm}
                            >
                                Confirm
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Task Detail Modal */}
            <TaskDetailModal
                task={selectedTask}
                isOpen={!!selectedTask}
                onClose={() => setSelectedTask(null)}
            />
        </div>
    );
};

export default ResourceReviewCard;

